import {ChangeDetectionStrategy, Component, DestroyRef} from '@angular/core';
import {CommunitiesService} from '../../_services/communities.service';
import {AuthService} from '../../_services/auth.service';
import {Community, UserCommunity} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {map, Observable} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {SnackbarService} from "../../_services/snackbar.service";
import {Title} from '@angular/platform-browser';
import {GoogleAnalyticsService} from "../../_services/google-analytics.service";
import {environment} from "../../../environments/environment";

@Component({
    selector: 'app-header-community-selector',
    templateUrl: './header-community-selector.component.html',
    styleUrl: './header-community-selector.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderCommunitySelectorComponent {
    communities: Community[] = [];
    selectedCommunity: Community | null = null;
    communitiesCount: number = 0;

    private isAdmin: boolean = this.authService.isSuperUser();

    constructor(
        private readonly communitiesService: CommunitiesService,
        private readonly authService: AuthService,
        private readonly localStorageService: LocalStorageService,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
        private readonly snackbarService: SnackbarService,
        private readonly route: ActivatedRoute,
        private readonly titleService: Title,
        private googleAnalyticsService: GoogleAnalyticsService
    ) {
        this.loadAndChooseCommunity();
    }

    selectOption(id: number): void {
        this.changeCommunity(this.communities.find((community: Community): boolean => community.id === id)!);
    }

    loadAndChooseCommunity(): void {
        const obs$: Observable<Community[]> = this.isAdmin
            ? this.communitiesService.getAllCommunities().pipe(map((data: Community[]): Community[] => data))
            : this.communitiesService.getUserCommunities().pipe(map((data: UserCommunity[]): Community[] => data.map(result => result.community)));
        obs$.pipe(
            takeUntilDestroyed(this.destroyRef)
        )
            .subscribe(res => {
                this.communities = res || [];
                this.selectedCommunity = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
                if (!this.selectedCommunity) {
                    // If no selected community then revert back to url checker
                    let url: string = window.location.href;
                    url = url.toLowerCase()
                    const validCommunity: Community = this.communities.find(community => (url.includes(community.front_end_url?.toLowerCase()) || community.front_end_url?.toLowerCase().includes(url) || community.front_end_url == url || url.includes(community.alternative_front_end_url?.toLowerCase()) || community.alternative_front_end_url?.toLowerCase().includes(url) || community.alternative_front_end_url == url)) ?? res[0];
                    this.changeCommunity(validCommunity);
                } else {
                    const validCommunity: Community = this.communities.find(community => community.id === this.selectedCommunity?.id) ?? res[0];
                    this.changeCommunity(validCommunity);
                }
            });
    }

    changeCommunity(community: Community): void {
        if (community) {
            this.communitiesService.setCurrentDashboardCommunity(community);
            this.titleService.setTitle(`${community.name} - ${this.getTitleFromRoute(this.route)}`);
            if (community?.google_analytics_id) {
                this.googleAnalyticsService.initialize(community?.google_analytics_id)
            } else {
                this.googleAnalyticsService.remove();
            }
            if (environment.masterGaId) {
                this.googleAnalyticsService.initialize(environment.masterGaId)
            }
            if (community?.id !== this.selectedCommunity?.id) {
                this.resetQueryParams();
                if (community?.home_page_path) {
                    this.router.navigateByUrl(community.home_page_path);
                } else {
                    this.router.navigate(['/dashboard']);
                }
            }
        } else {
            this.snackbarService.openErrorSnackBar('You are currently not in any communities');
        }
        this.selectedCommunity = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
        this.communitiesCount = this.communities?.length || 0;
    }

    resetQueryParams(): void {
        if (Object.keys(this.route.snapshot.queryParams).length > 0) {
            const path: string = this.router.url.split('?')[0];
            this.router.navigate([path], {queryParams: {}});
        }
    }

    private getTitleFromRoute(route: ActivatedRoute): string {
        let title: string = route.snapshot?.title ?? 'Explain';
        if (route.firstChild) {
            title = this.getTitleFromRoute(route.firstChild) || title;
        }
        return title;
    }
}
